<template>
  <div :class="wrapperClasses" v-if="notifications.length > 0">
    <div class="cube-notifications__mark-all" v-if="notifications.length > 0">
      <h4 class="cube-notifications__title">
        {{ str('notifications.title') }}
      </h4>
      <a :class="markAllLinkClasses" @click="markAllAsRead()">
        {{ str('notifications.markAllAsRead') }}
      </a>
    </div>
    <div :class="contentClasses">
      <ul class="cube-notifications__list">
        <li
          v-for="notification in limitedNotifications"
          class="cube-notifications__list-item"
          :key="notification.id"
        >
          <a
            :class="notificationClasses(notification)"
            @click="markAsRead(notification)"
            :title="str('notifications.markAsRead')"
          >
            <span>
              {{ notification.body }}<br />
              {{ formatTimeAgo(notification.created_at) }}
            </span>
          </a>
        </li>
      </ul>
      <div
        class="cube-notifications__more"
        v-if="notifications.length > maxNotifications"
      >
        <a
          class="cube-notifications__more-link button button--secondary button--small"
          @click="maxNotifications += increaseBy"
        >
          {{ str('notifications.showMore') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'cube-notifications',
    props: {
      notifications: {
        type: Array,
        required: true,
      },
      cubeName: {
        type: String,
        required: true,
      },
      extraClasses: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        maxNotifications: 2,
        increaseBy: 4,
        visible: false,
      }
    },
    mounted() {
      if (this.unreadNotifications.length > 0) {
        this.visible = true
      }
    },
    computed: {
      wrapperClasses() {
        let result = ['cube-notifications']
        return result.concat(this.extraClasses)
      },
      contentClasses() {
        let result = ['cube-notifications__content']
        if (this.visible) {
          result.push('cube-notifications__content--visible')
        }
        return result.join(' ')
      },
      markAllLinkClasses() {
        let result = ['cube-notifications__mark-all-link']
        if (this.visible) {
          result.push('cube-notifications__mark-all-link--visible')
        }
        return result.join(' ')
      },
      limitedNotifications() {
        if (this.notifications.length < this.maxNotifications) {
          return this.notifications
        } else {
          return this.notifications.slice(0, this.maxNotifications)
        }
      },
      unreadNotifications() {
        return this.notifications.filter((n) => n.unread === 1)
      },
    },
    methods: {
      notificationClasses(notification) {
        let result = ['cube-notifications__notification']
        if (notification.unread === 1) {
          result.push('cube-notifications__notification--unread')
        }
        return result.join(' ')
      },
      markAsRead(notification) {
        this.$store.dispatch('markNotificationsAsRead', {
          notification: notification,
        })
      },
      markAllAsRead() {
        this.$store.dispatch('markNotificationsAsRead', {
          fromCubeName: this.cubeName,
        })
      },
    },
  }
</script>

<style lang="scss">
  .cube-notifications {
    margin: spacer(1) 0 spacer(4);
    width: 100%;

    &__title {
      margin: 0;
      font-size: $font-size-base;
      transition: $transition-base;
      display: inline-flex;
      align-items: center;
      color: $grey-dark;
    }

    &__has-unread {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      width: 0.75em;
      height: 0.75em;
      background: $notification-color;
      padding: 0.25em;
      border-radius: 100%;
      color: $white;
      margin-right: spacer(0.5);
    }

    &__mark-all {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      overflow: hidden;

      &-link {
        transform: translateY(100%);
        transition: $transition-base, transform 0.3s ease-in-out 0s;

        &--visible {
          transform: translateY(0%);
          transition: $transition-base, transform 0.3s ease-in-out 0.3s;
        }
      }
    }

    &__content {
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease-in-out 0.3s;

      &--visible {
        max-height: 100vh;
        transition: max-height 0.3s ease-in-out 0s;

        @media (min-height: $bp-xl) {
          max-height: 50vh;
        }
      }
    }

    &__list {
      list-style: none;
      margin: spacer(0.5) 0 0;
      border-left: 4px solid $community;
      box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.1);
      padding: spacer(1) spacer(3) spacer(1) spacer(2);

      &-item {
        font-weight: bold;
        border-bottom: 1px solid $grey-lightest;

        &:last-child {
          border-bottom: none;
        }
      }
    }

    &__notification {
      cursor: pointer;
      transition: $transition-base;
      display: flex;
      justify-content: space-between;
      align-items: center;
      line-height: $line-height-sm;
      padding: spacer(1) 0;
      font-weight: normal;

      &:hover,
      &:active,
      &:focus {
        color: $grey-dark;
      }

      &--unread {
        font-weight: bold;

        &::after {
          content: '';
          display: block;
          border-radius: 100%;
          width: spacer(0.75);
          height: spacer(0.75);
          background: $notification-color;
          flex-shrink: 0;
          margin-left: spacer(1);
        }
      }
    }

    &__more {
      margin-top: spacer(1);
      text-align: center;
    }
  }
</style>
